import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetCountry, GetProvince } from "api/rpc/2024-04/country";
import {
  GetTeeOnPaymentAccounts,
  GetTeeOnPaymentRepresentative,
  PostTeeOnPaymentRepresentative,
  PutAcceptTerms,
  PutTeeOnPaymentAccounts,
  TPostTeeOnPaymentRepresentative,
} from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/accounts";

import { showError, showSuccess } from "redux/actions/ui";
import { ICountry, IProvince } from "redux/reducers/models/customer";
import { TTeeOnPaymentRepresentative } from "redux/reducers/models/transaction";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import InnerLayout from "components/innerLayout/InnerLayout";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import { Select } from "components/select";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import { useHistory } from "react-router";
import { isEqualWith, isNull } from "lodash";
// import { unsavedChangesExist } from "helpers/Helpers";

interface ICompanyInformationState {
  company_name: string;
  email: string;
  company_address_line_1: string;
  company_city: string;
  company_country_code: string;
  company_postal: string;
  company_province_name: string;
  company_phone: string;
  company_registration_number: string;
  company_directors_provided: string;
  company_executives_provided: string;
  company_owners_provided: string;
}

interface IBusinessProfileState {
  business_profile_name: string;
  business_profile_mcc: any;
  business_profile_product_description: string;
  business_profile_support_email: string;
  business_profile_support_phone: string;
  business_profile_support_url: string;
  business_profile_url: string;
  // business_profile_privacy_policy_url: string;
}

export default function TeeOnPaymentsEdit() {
  const { t } = useTranslation();
  const history = useHistory();
  const { Option } = Select;

  const dispatch = useAppDispatch();

  const business_types = [
    {
      title: t("secure.facility.settings.facility.facility_edit.001"),
      value: 7941,
    },
    {
      title: t("secure.facility.settings.facility.facility_edit.002"),
      value: 5734,
    },
    {
      title: t("secure.facility.settings.facility.facility_edit.003"),
      value: 7011,
    },
  ];

  const [countries, setCountries] = useState<ICountry[]>([]);
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [teeOnPaymentLoaded, setTeeOnPaymentLoaded] = useState<boolean>(false);

  const [businessInformationBeforeChanges, setBusinessInformationBeforeChanges] =
    useState<IBusinessProfileState>(undefined);

  const [companyInformationBeforeChanges, setCompanyInformationBeforeChanges] =
    useState<ICompanyInformationState>(undefined);

  const [companyInformation, setCompanyInformation] = useState<ICompanyInformationState>({
    company_name: "",
    email: "",
    company_address_line_1: "",
    company_city: "",
    company_country_code: "",
    company_province_name: "",
    company_postal: "",
    company_phone: "",
    company_registration_number: "",
    company_directors_provided: "",
    company_executives_provided: "",
    company_owners_provided: "",
  });

  const [businessInformation, setBusinessInformation] = useState<IBusinessProfileState>({
    business_profile_name: "",
    business_profile_mcc: "",
    business_profile_product_description: "",
    business_profile_support_email: "",
    business_profile_support_phone: "",
    business_profile_support_url: "",
    business_profile_url: "",
    // business_profile_privacy_policy_url: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    // void loadCountries(source.token);
    void loadTeeOnPaymentsAccounts(source.token);

    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (companyInformation.company_country_code) {
      if (countries) {
        const country = countries?.find(country => country.code === companyInformation.company_country_code);
        void loadProvinces(country?.id, source.token);
      }
    } else {
      setProvinces([]);
    }
  }, [companyInformation.company_country_code]);

  async function loadTeeOnPaymentsAccounts(token?: CancelToken) {
    const res = await GetTeeOnPaymentAccounts(null, true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.payments.manage.009")));
    }

    const countryRes = await GetCountry(null, true, token);
    if (token && token.reason) {
      return;
    }
    if (countryRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading countries.")); // TODO: Translation
    }

    setCountries(countryRes.data);
    setCompanyInformation(prevState => ({
      ...prevState,
      company_name: res.data?.company_name,
      email: res.data?.email,
      company_address_line_1: res.data?.company_address_line_1,
      company_city: res.data?.company_city,
      company_country_code: res.data.country_code,
      company_province_name: res.data?.company_province_name,
      company_postal: res.data?.company_postal,
      company_phone: res.data?.company_phone,
      company_registration_number: res.data?.company_registration_number,
      company_directors_provided: res.data?.company_directors_provided,
      company_executives_provided: res.data?.company_executives_provided,
      company_owners_provided: res.data?.company_owners_provided,
    }));

    setBusinessInformation(prevState => ({
      ...prevState,
      business_profile_name: res.data?.business_profile_name,
      business_profile_mcc: Number(res.data?.business_profile_mcc),
      business_profile_product_description: res.data?.business_profile_product_description,
      business_profile_support_email: res.data?.business_profile_support_email,
      business_profile_support_phone: res.data?.business_profile_support_phone,
      business_profile_support_url: res.data?.business_profile_support_url,
      business_profile_url: res.data?.business_profile_url,
      // business_profile_privacy_url: res.data?.business_profile_privacy_policy_url,
    }));

    setTeeOnPaymentLoaded(true);
  }

  /* Called only when country_id changes & not nullish */
  async function loadProvinces(countryId: string | number, token?: CancelToken) {
    if (provinces !== undefined) {
      setProvinces(undefined);
    }
    const res = await GetProvince({ country_id: Number(countryId) }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error loading provinces.")); // TODO: Translation
    }

    setProvinces(res.status !== StatusCode.OK ? [] : res.data);
  }

  function handleCompanyInputChange(event: any) {
    const { id, value } = event.target;
    setCompanyInformation(prevState => ({ ...prevState, [id]: value }));
  }

  function handleBusinessInputChange(event: any) {
    const { id, value } = event.target;
    setBusinessInformation(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: any) {
    setBusinessInformation(prevState => ({ ...prevState, business_profile_mcc: value }));
  }

  async function saveTeeOnPayment() {
    // Allows:
    // h2t-1b8
    // h2z 1b8
    // H2Z1B8

    // Disallows:
    // Z2T 1B8 (leading Z)
    // H2T 1O3 (contains O)
    // Leading Z,W or to contain D, F, I, O, Q or U
    const postalRegex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

    const match = postalRegex.exec(companyInformation?.company_postal);

    if (!match) {
      console.log("Postal Code Invalid");
      dispatch(showError("Invalid Postal Code"));
      return;
    }

    const params = {
      company_name: companyInformation.company_name,
      email: companyInformation.email,
      company_address_line_1: companyInformation.company_address_line_1,
      company_city: companyInformation.company_city,
      company_country_code: companyInformation.company_country_code,
      company_province_name: companyInformation.company_province_name,
      company_postal: companyInformation.company_postal,
      company_phone: companyInformation.company_phone,
      company_registration_number: companyInformation.company_registration_number,
      company_directors_provided: companyInformation.company_directors_provided,
      company_executives_provided: companyInformation.company_executives_provided,
      company_owners_provided: companyInformation.company_owners_provided,
      business_profile_name: businessInformation.business_profile_name,
      business_profile_mcc: String(businessInformation.business_profile_mcc),
      business_profile_product_description: businessInformation.business_profile_product_description,
      business_profile_support_email: businessInformation.business_profile_support_email,
      business_profile_support_phone: businessInformation.business_profile_support_phone,
      business_profile_support_url: businessInformation.business_profile_support_url,
      business_profile_url: businessInformation.business_profile_url,
      // business_privacy_policy_url: businessInformation.business_profile_privacy_policy_url,
    };

    const putPaymentRes = await PutTeeOnPaymentAccounts(params, true);

    if (putPaymentRes.status !== StatusCode.OK) {
      dispatch(showError("Error saving Tee On Payment")); // TODO: Translation
      return;
    }

    setCompanyInformationBeforeChanges(companyInformation);
    setBusinessInformationBeforeChanges(businessInformation);
  }

  function unsavedChangesExist() {
    if (businessInformationBeforeChanges === undefined) {
      if (teeOnPaymentLoaded) {
        setBusinessInformationBeforeChanges(businessInformation);
      }
      return false;
    }

    if (companyInformationBeforeChanges === undefined) {
      if (teeOnPaymentLoaded) {
        setCompanyInformationBeforeChanges(companyInformation);
      }
      return false;
    }

    return (
      !isEqualWith(businessInformation, businessInformationBeforeChanges, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      }) ||
      !isEqualWith(companyInformation, companyInformationBeforeChanges, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      })
    );
  }

  function handleCountryDropdown(value: any) {
    setCompanyInformation(prev => ({
      ...prev,
      company_country_code: value,
    }));
  }

  return (
    <Page
      title={"Edit Merchant Account"}
      breadcrumbs={[
        {
          prefix: true,
          label: "Tee On Payments",
          url: "/admin/settings/payments/manage/tee-on-payments",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: () => saveTeeOnPayment(),
        onCancel: () => {
          setBusinessInformation(businessInformationBeforeChanges);
          setCompanyInformation(companyInformationBeforeChanges);
        },
      }}
    >
      <Card>
        <Card.Section title="Company Information" subtitle="Enter in the details related to the business entity">
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={companyInformation.company_name ?? ""}
                label={"Legal Business Name"}
                id="company_name"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"Name..."}
                helpText={"Enter in the name of the business that matches the account's business number"}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={companyInformation.email ?? ""}
                label={"Contact Email"}
                id="email"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"Email..."}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={companyInformation.company_address_line_1 ?? ""}
                label={"Address Line 1"}
                id="company_address_line_1"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"Address..."}
              />
              <Input
                value={companyInformation.company_city ?? ""}
                label={"City"}
                id="company_city"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"City..."}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                label={"Country"}
                onChange={(value: any, country: any) => handleCountryDropdown(value)}
                defaultValue={companyInformation?.company_country_code}
              >
                {countries.map((country: any) => {
                  return (
                    <Select.Option key={country.id} value={country.code} name={country.name} extraValues={country}>
                      {country.name}
                    </Select.Option>
                  );
                })}
              </Select>

              <Select
                label={t("secure.facility.settings.payments.tee_on_payments.029")}
                onChange={(value: string) =>
                  setCompanyInformation(prev => ({
                    ...prev,
                    company_province_name: value,
                  }))
                }
                defaultValue={companyInformation?.company_province_name}
              >
                {provinces === undefined && (
                  <Select.Option value={"loading"} disabled>
                    <div style={{ height: "40px", padding: "8px 16px" }}>
                      <Spin />
                    </div>
                  </Select.Option>
                )}
                {provinces?.map(province => {
                  return (
                    <Select.Option key={province.id} value={province.name} name={province.name}>
                      {province.name}
                    </Select.Option>
                  );
                })}
              </Select>

              <Input
                value={companyInformation.company_postal ?? ""}
                label={"Postal"}
                id="company_postal"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"Postal Code..."}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={companyInformation.company_phone ?? ""}
                label={"Phone"}
                id="company_phone"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"Phone..."}
              />

              <Input
                value={companyInformation.company_registration_number ?? ""}
                label={"Business Registration Number"}
                id="company_registration_number"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={"Company Registration #..."}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        <Card.Section
          title="Business Profile"
          subtitle="This information are details related to the business that may be customer facing"
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_name ?? ""}
                label={"Business Name"}
                id="business_profile_name"
                onChange={e => handleBusinessInputChange(e)}
                placeholder={"Business Name..."}
                helpText={"The operating name of the company, if it's different than the legal business name"}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_url ?? ""}
                label={"Business URL"}
                id="business_profile_url"
                onChange={e => handleBusinessInputChange(e)}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <span className="facility-edit-input_container">
                <Select
                  label={"Merchant Code"}
                  onChange={(value: any) => handleDropDownChange(value)}
                  defaultValue={Number(businessInformation.business_profile_mcc)}
                >
                  {business_types.map((business: any, index: number) => {
                    return (
                      <Option key={index} value={business.value} name={business.title}>
                        {business.title}
                      </Option>
                    );
                  })}
                </Select>
              </span>
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_support_email ?? ""}
                label={"Support Email"}
                id="business_profile_support_email"
                onChange={e => handleBusinessInputChange(e)}
              />
              <Input
                value={businessInformation.business_profile_support_phone ?? ""}
                label={"Support Phone"}
                id="business_profile_support_phone"
                onChange={e => handleBusinessInputChange(e)}
              />
              <Input
                value={businessInformation.business_profile_support_url ?? ""}
                label={"Support URL"}
                id="business_profile_support_url"
                onChange={e => handleBusinessInputChange(e)}
              />
            </FormLayout.Group>

            {/* <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_privacy_policy_url ?? ""}
                label={"Privacy Policy URL"}
                id="business_profile_privacy_policy_url"
                onChange={(e) => handleBusinessInputChange(e)}
              />
            </FormLayout.Group> */}

            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_product_description ?? ""}
                label={"Product Decription"}
                id="business_profile_product_description"
                onChange={e => handleBusinessInputChange(e)}
                placeholder={"Product Description..."}
                type={"text"}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
